/**
 * NewImportPage — the v37 New Import wizard (sub-task 03).
 *
 * Four labelled steps (1 Upload · 2 Link to Search · 3 Preview · 4 Column
 * Mapping) with the primary CTA "Import N contacts". On submit it runs the
 * presigned-PUT → complete handshake (via `useNewImport`), surfaces the submit
 * states (`idle → uploading → finalizing → processing`) with an XHR-driven
 * progress bar, and on PUT failure shows an error + retry. Cancel returns to the
 * Imports list.
 *
 * Where `thrive-search-import` is visible (released on every stage
 * 2026-10-20) a Source step comes first, and its default is "Pull from a
 * Thrive search" (`ThriveImportFlow`, epic 2026-10-15-thrive-search-import,
 * sub-task 03). "Upload a file" brings back the four steps above, unchanged.
 * With the flag off there is no Source step and the page is exactly the
 * upload wizard.
 *
 * Tokenized: token color utilities + `text-*` roles + Tailwind's 4px scale.
 * No inline styles, no arbitrary `text-[Npx]`.
 */

import { Button, ProgressBar } from "@nu-assist/ui";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  type ImportsClient,
  type SearchListResponseDTO,
  type SearchStatusFilter,
  createImportsClient,
} from "../../../clients/imports.client";
import { activeFlags } from "../../../config/featureFlags";
import { usePreviewModeIfMounted } from "../../../features/PreviewProvider";
import { pageVisible } from "../../../features/useFeatureAccess";
import { ThriveImportFlow } from "./ThriveImportFlow";
import { type PreviewData, allRowsSkippedReason, parsePreviewFile } from "./parsePreview";
import { ColumnMappingStep } from "./steps/ColumnMappingStep";
import {
  LinkSearchStep,
  type SearchTypeaheadOption,
  type SearchTypeaheadResult,
} from "./steps/LinkSearchStep";
import { PreviewStep } from "./steps/PreviewStep";
import { type ImportSource, SourceStep } from "./steps/SourceStep";
import { UploadStep } from "./steps/UploadStep";
import { type PutToS3, type SubmitState, useNewImport } from "./useNewImport";

const STEP_LABELS = ["1 Upload", "2 Link to Search", "3 Preview", "4 Column Mapping"] as const;

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

const SUBMIT_LABELS: Record<SubmitState, string> = {
  idle: "",
  uploading: "Uploading…",
  finalizing: "Finalizing…",
  processing: "Processing…",
  error: "Upload failed",
  // The CTA is DISABLED in this state (see the submit button): re-submitting
  // the refused file would re-presign, re-PUT and be refused again — a Retry
  // in disguise, one more orphan object per click. Choosing another file
  // resets to idle.
  rejected: "Choose another file",
};

/** States in which the CTA reads as "Import N contacts" again — the pipeline
 *  is not running and the user can act on the selected file. */
const SETTLED_STATES: ReadonlySet<SubmitState> = new Set(["idle", "error"]);

export interface NewImportPageProps {
  /** Injected for tests; defaults to the XHR uploader inside the hook. */
  putToS3?: PutToS3;
  /** Injected for tests; the imports client API origin. */
  apiBase?: string;
  /** Injected for tests; a fully-faked imports client. */
  client?: ImportsClient;
  /** Injected for tests; overrides the live Thrive search fetch with a
   * static in-memory list (filtered/capped locally, same rules as the
   * backend: 2-char minimum, ID exact or case-insensitive name substring,
   * cap at 10). */
  searchOptions?: SearchTypeaheadOption[];
}

const SEARCH_RESULT_CAP = 10;

/** Wire option → typeahead option, summary fields included. */
function toTypeaheadResult(res: SearchListResponseDTO): SearchTypeaheadResult {
  return {
    items: (res.items ?? []).map((o) => ({
      id: o.id,
      name: o.name,
      jobTitle: o.job_title ?? null,
      company: o.company ?? null,
      isTest: o.is_test,
      status: o.status ?? null,
      candidaciesCount: o.candidacies_count ?? null,
      thriveCreatedAt: o.thrive_created_at ?? null,
      closedAt: o.closed_at ?? null,
    })),
    total: res.total ?? res.items?.length ?? 0,
  };
}

/** The status filter's wire value: the API reads `all` and the four statuses. */
const STATUS_WIRE: Record<SearchStatusFilter, string> = {
  closed: "closed",
  open: "open",
  bd: "bd",
  lost: "lost",
  all: "all",
};

/** Client-side stand-in for `search()` when `searchOptions` is test-injected —
 * mirrors the backend's `domain.filter_records` + cap rule closely enough for
 * component tests (2-char minimum, ID exact OR case-insensitive substring). */
/** `statuses` filter for test-injected options (the wire does it server-side). */
function statusMatches(option: SearchTypeaheadOption, status: SearchStatusFilter): boolean {
  if (status === "all") return true;
  const wanted: Record<Exclude<SearchStatusFilter, "all">, string> = {
    closed: "Closed",
    open: "Open",
    bd: "Business Development",
    lost: "Lost",
  };
  return option.status === wanted[status];
}

function staticSearch(
  options: SearchTypeaheadOption[]
): (query: string) => Promise<SearchTypeaheadResult> {
  return async (query) => {
    const trimmed = query.trim();
    const q = trimmed.toLowerCase();
    const filtered =
      trimmed.length < 2
        ? options
        : options.filter(
            (o) =>
              o.id === trimmed ||
              o.name.toLowerCase().includes(q) ||
              (o.jobTitle?.toLowerCase().includes(q) ?? false) ||
              (o.company?.toLowerCase().includes(q) ?? false)
          );
    return { items: filtered.slice(0, SEARCH_RESULT_CAP), total: filtered.length };
  };
}

export function NewImportPage({
  putToS3,
  apiBase,
  client,
  searchOptions,
}: NewImportPageProps = {}) {
  const navigate = useNavigate();

  // Resolve the same imports client the upload handshake uses, for the
  // Link-to-Search picker's typeahead fetch (GET /api/web/v2/searches?q= →
  // nu-assist's own Postgres mirror of Thrive, Open-status + TEST whitelist).
  const resolvedClient = useMemo(
    () => client ?? createImportsClient({ apiBase: apiBase ?? API_BASE }),
    [client, apiBase]
  );
  const liveSearch = useCallback(
    async (query: string): Promise<SearchTypeaheadResult> => {
      // Optional-chain so a partial test client without listSearches
      // resolves to an empty result rather than throwing.
      const res = await (resolvedClient.listSearches?.(query ? { q: query } : {}) ??
        Promise.resolve({ items: [], total: 0 }));
      return toTypeaheadResult(res);
    },
    [resolvedClient]
  );
  // The Thrive-source picker's fetch: the same endpoint with `statuses`.
  // Test-injected options are filtered by status locally, like `staticSearch`.
  const sourceSearch = useCallback(
    async (query: string, status?: SearchStatusFilter): Promise<SearchTypeaheadResult> => {
      if (searchOptions) {
        const res = await staticSearch(searchOptions)(query);
        const items = res.items.filter((o) => status === undefined || statusMatches(o, status));
        return { items, total: items.length };
      }
      const res = await (resolvedClient.listSearches?.({
        ...(query ? { q: query } : {}),
        ...(status ? { statuses: STATUS_WIRE[status] } : {}),
      }) ?? Promise.resolve({ items: [], total: 0 }));
      return toTypeaheadResult(res);
    },
    [resolvedClient, searchOptions]
  );
  // The Source step exists only where `thrive-search-import` is visible
  // (released on every stage). The same gate as `FeatureRoute`;
  // `usePreviewModeIfMounted` because this page renders in suites that mount
  // no `PreviewProvider` (the person panel's gate does the same).
  const thriveSourceEnabled = pageVisible(
    activeFlags(),
    usePreviewModeIfMounted(),
    "linkedin",
    "thrive-search-import"
  );
  const [source, setSource] = useState<ImportSource>("thrive");
  const showThrive = thriveSourceEnabled && source === "thrive";
  // Test-injected options win (filtered/capped locally); otherwise the live
  // debounced Thrive typeahead.
  const resolvedSearch = useMemo(
    () => (searchOptions ? staticSearch(searchOptions) : liveSearch),
    [searchOptions, liveSearch]
  );
  const [file, setFile] = useState<File | null>(null);
  const [fromBabyChops, setFromBabyChops] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [searchName, setSearchName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const { submit, retry, reset, state, progress, error, isBusy } = useNewImport({
    putToS3,
    apiBase,
    client,
  });

  async function handleFileSelected(selected: File, fromNucce: boolean) {
    setFile(selected);
    setFromBabyChops(fromNucce);
    setParseError(null);
    // A new file after a refusal or a failure starts clean: the message under
    // the CTA was about the previous file, and `retry()` must not resend it.
    if (state === "rejected" || state === "error") reset();
    try {
      setPreview(await parsePreviewFile(selected));
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "Could not read the file.");
      setPreview(null);
    }
  }

  // THE NUMBER THE RECRUITER COMMITS TO IS THE NUMBER THAT LANDS: rows in
  // the file minus the rows the preview already knows the worker will drop
  // (no `/in/` LinkedIn URL). The CTA, the toast and the gate below all read
  // this one value, so the preview cannot say "1 row skipped" and the toast
  // "3 contacts imported" for the same file (/pr-review #971 iter 1, C I1).
  const landingCount = preview ? Math.max(0, preview.total - preview.skippedNoLinkedin) : 0;
  // A file the row rule would EMPTY: it HAS the `LinkedIn URL` column and
  // every row lacks a `/in/` URL. Nothing would land, so nothing is submitted;
  // the worker refuses the same file server-side for the paths this wizard
  // does not front. ONLY WITH THE COLUMN PRESENT: a file without it is not
  // this case but the header refusal's — `/complete` answers 422 naming the
  // missing column and the ChopLink columns — and the CTA must stay enabled
  // for that answer to be reachable. The first version gated on the count
  // alone, and the count was "every row" for a column-less file, so the
  // incident file (Recruiter export, URLs under `Profile URL`) was told to
  // re-capture profiles instead of being refused with the right diagnosis
  // (/pr-review #971 iter 2, Agent A section 2.1 / Agent C C2).
  // SO, FOR A COLUMN-LESS FILE, THIS IS WHAT YOU SEE AND IT IS BY DESIGN: the
  // mapping step reads "Missing: LinkedIn URL", the CTA reads "Import N
  // contacts" (N = rows in the file), the file is presigned and PUT, and only
  // then `/complete` refuses it with 422 and the header reason. The header
  // verdict has ONE owner (`viability.py`, its prose pinned by
  // `test_import_refused_example.py`); the wizard does not grow a second copy
  // of that sentence, so it lets the server say it. Do not "fix" this back
  // into a client-side gate (/pr-review #971 iter 3, Agent C M16).
  const nothingToImport = preview?.hasUrlColumn === true && preview.total > 0 && landingCount === 0;

  const ctaLabel = useMemo(() => {
    if (!SETTLED_STATES.has(state)) return SUBMIT_LABELS[state];
    if (preview) return `Import ${landingCount.toLocaleString()} contacts`;
    return "Import contacts";
  }, [preview, state, landingCount]);

  function handleSubmit() {
    // Search is REQUIRED for the MVP (Compare/Export are scoped to a search).
    if (!file || !preview || !searchId || nothingToImport) return;
    // `searchName` is captured directly from the typeahead's `onChange` at
    // selection time (no separate full-list lookup needed — the typeahead
    // never holds a complete list to look up against).
    submit({
      file,
      source: fromBabyChops ? "baby_chops" : "csv_upload",
      importFormat: "baby_chops_v1",
      searchIdThrive: searchId,
      searchNameThrive: searchName,
      contactCount: landingCount,
    });
  }

  return (
    <div data-testid="new-import-page" className="p-12">
      <nav aria-label="Breadcrumb" className="mb-3 text-caption text-text-secondary">
        <button
          type="button"
          onClick={() => navigate("/research/imports")}
          className="hover:text-accent"
        >
          People Lists
        </button>
        <span className="px-1">/</span>
        <span className="text-text">New Import</span>
      </nav>

      <h1 className="text-h1 text-text mb-6">New Import</h1>

      {showThrive ? (
        <ThriveImportFlow
          client={resolvedClient}
          searchSource={sourceSearch}
          searchTarget={resolvedSearch}
          sourceStep={<SourceStep value={source} onChange={setSource} />}
        />
      ) : (
        <>
          {/* Step rail */}
          <ol className="mb-8 flex flex-wrap gap-2" data-testid="step-rail">
            {STEP_LABELS.map((label) => (
              <li
                key={label}
                className="rounded-md bg-surface px-3 py-1.5 text-caption text-text-secondary"
              >
                {label}
              </li>
            ))}
          </ol>

          <div className="flex flex-col gap-8 max-w-3xl">
            {thriveSourceEnabled ? <SourceStep value={source} onChange={setSource} /> : null}
            <section aria-label="Upload">
              <UploadStep
                onFileSelected={handleFileSelected}
                error={uploadError}
                onError={setUploadError}
              />
              {parseError ? (
                <p data-testid="parse-error" className="mt-2 text-caption text-error">
                  {parseError}
                </p>
              ) : null}
            </section>

            <section aria-label="Link to Search">
              <LinkSearchStep
                value={searchId}
                valueName={searchName}
                onChange={(id, name) => {
                  setSearchId(id);
                  setSearchName(name);
                }}
                search={resolvedSearch}
              />
            </section>

            {preview ? (
              <>
                <section aria-label="Preview">
                  <PreviewStep preview={preview} fromBabyChops={fromBabyChops} />
                </section>
                <section aria-label="Column Mapping">
                  <ColumnMappingStep mapping={preview.mapping} />
                </section>
              </>
            ) : null}

            {/* Upload progress bar (XHR upload-progress) */}
            {state === "uploading" ? (
              <div data-testid="upload-progress">
                <ProgressBar
                  value={progress}
                  max={1}
                  label="Upload progress"
                  testId="upload-progress-bar"
                />
                <p className="mt-1 text-caption text-text-secondary">
                  {SUBMIT_LABELS.uploading} {Math.round(progress * 100)}%
                </p>
              </div>
            ) : null}

            {/* Non-uploading submit states */}
            {state === "finalizing" || state === "processing" ? (
              <p data-testid="submit-state" className="text-caption text-text-secondary">
                {SUBMIT_LABELS[state]}
              </p>
            ) : null}

            {/* 422 on /complete → the server declined the FILE. No import was
            created and there is nothing to retry: the reason is shown where
            the user is looking and they choose another file. */}
            {state === "rejected" ? (
              <div
                data-testid="import-refused"
                role="alert"
                className="flex flex-col gap-1 rounded-md border-l-4 border-error-strong bg-error-muted p-4"
              >
                <p className="text-body-sm font-medium text-error">This file was not imported</p>
                <p className="text-caption text-text">{error}</p>
                <p className="text-caption text-text-secondary">
                  Nothing was created. Choose a different file to try again.
                </p>
              </div>
            ) : null}

            {/* PUT failure → error + retry */}
            {state === "error" ? (
              <div data-testid="submit-error" className="flex items-center gap-3">
                <p className="text-caption text-error">{error}</p>
                <Button variant="secondary" onClick={retry} data-testid="retry-button">
                  Retry
                </Button>
              </div>
            ) : null}

            {/* Required-search hint: the CTA is gated on a linked search (MVP). */}
            {preview && !searchId ? (
              <p data-testid="search-required-hint" className="text-caption text-warning">
                Link a Thrive search to continue — every import is scoped to a search.
              </p>
            ) : null}

            {/* Nothing would land: every row lacks a LinkedIn URL. Said in place,
            like the refusal, in the worker's own words for the same file, and
            the CTA stays disabled — a 0-contact import is not something to
            create and then explain. */}
            {nothingToImport && preview ? (
              <p data-testid="nothing-to-import" className="text-caption text-error">
                {allRowsSkippedReason(preview.skippedNoLinkedin)}
              </p>
            ) : null}

            <div className="flex gap-3">
              <Button
                onClick={handleSubmit}
                disabled={
                  !preview || !searchId || isBusy || state === "rejected" || nothingToImport
                }
                data-testid="submit-button"
              >
                {ctaLabel}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate("/research/imports")}
                disabled={isBusy}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
